import { useMemo, useState, useCallback } from "react";
import { useGetOne, useDataProvider } from "react-admin";
import { useQuery } from "@tanstack/react-query";
import type { RaRecord } from "react-admin";
import {
  validateTemplateParameters,
  getMissingParametersMessage,
  extractTemplateVariables,
  OUTSTANDING_PREVIEW_FETCH_LIMIT,
  type BroadcastParameter,
  type FilterCriteriaShape,
  type FilterGroupCriteriaShape,
} from "./utils";
import {
  hasMiqaatRsvpAudienceIncluded,
  isMiqaatRsvpFilterEnvelope,
} from "./miqaat-rsvp-filter-criteria";

export { useIsFmbOutstandingBroadcastEnabled } from "@/hooks/useIsFmbOutstandingBroadcastEnabled";
export { useIsSabilOutstandingBroadcastEnabled } from "@/hooks/use-is-sabil-outstanding-broadcast-enabled";
export { useIsMuttavatteenOutstandingBroadcastEnabled } from "@/hooks/use-is-muttavatteen-outstanding-broadcast-enabled";

export const useTemplate = (templateName: string | undefined) =>
  useGetOne("whatsappTemplates", {
    id: templateName || "",
  });

export type PreviewRecipientsParams = {
  filterCriteria: FilterGroupCriteriaShape | undefined | null;
  limit?: number;
  offset?: number;
  enabled?: boolean;
};

export const usePreviewRecipients = ({
  filterCriteria,
  limit = 10000,
  offset = 0,
  enabled = false,
}: PreviewRecipientsParams) => {
  const dataProvider = useDataProvider();

  return useQuery({
    queryKey: ["whatsappBroadcasts", "previewRecipients", filterCriteria, limit, offset],
    queryFn: async () => {
      if (!filterCriteria || (!filterCriteria.rules?.length && !filterCriteria.groups?.length)) {
        throw new Error("Please add at least one filter condition");
      }
      return dataProvider.previewRecipients("whatsappBroadcasts", {
        filterCriteria,
        limit,
        offset,
      });
    },
    enabled: enabled && !!filterCriteria,
    staleTime: 30000,
  });
};

export const usePreviewMiqaatRsvpRecipients = ({
  miqaatId,
  filterCriteria,
  recipientItsIds,
  recipientPhoneNumbers,
  limit = 10000,
  offset = 0,
  enabled = false,
}: {
  miqaatId: string | undefined;
  filterCriteria: FilterCriteriaShape | undefined | null;
  recipientItsIds?: string[] | null;
  recipientPhoneNumbers?: string[] | null;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) => {
  const dataProvider = useDataProvider();

  const isEnvelope = isMiqaatRsvpFilterEnvelope(filterCriteria);
  const hasFilter = Boolean(
    filterCriteria &&
    (isEnvelope
      ? hasMiqaatRsvpAudienceIncluded(filterCriteria)
      : ("rules" in filterCriteria && (filterCriteria.rules?.length ?? 0) > 0) ||
        ("groups" in filterCriteria && (filterCriteria.groups?.length ?? 0) > 0))
  );
  const hasIts = Array.isArray(recipientItsIds) && recipientItsIds.length > 0;
  const hasPhones =
    Array.isArray(recipientPhoneNumbers) && recipientPhoneNumbers.some((p) => String(p).trim());

  const selectionKey = JSON.stringify({
    miqaatId,
    // Only include audience inputs that the caller passed. Advanced-filter preview
    // must not feed selected phones back into this key (infinite refetch).
    filterCriteria: hasFilter ? filterCriteria : null,
    recipientItsIds: hasIts ? recipientItsIds : null,
    recipientPhoneNumbers: hasPhones ? recipientPhoneNumbers : null,
  });

  return useQuery({
    queryKey: ["whatsappBroadcasts", "previewMiqaatRsvpRecipients", selectionKey, limit, offset],
    queryFn: async () => {
      if (!miqaatId?.trim()) {
        throw new Error("Select a miqaat first");
      }
      if (!hasFilter && !hasIts && !hasPhones) {
        throw new Error("Add filter rules, ITS numbers, or phone recipients first");
      }
      return dataProvider.previewMiqaatRsvpRecipients("whatsappBroadcasts", {
        miqaatId: miqaatId.trim(),
        filterCriteria: hasFilter ? filterCriteria : undefined,
        recipientItsIds: hasIts ? (recipientItsIds ?? undefined) : undefined,
        recipientPhoneNumbers: hasPhones ? (recipientPhoneNumbers ?? undefined) : undefined,
        limit,
        offset,
      });
    },
    enabled: Boolean(enabled && miqaatId?.trim() && (hasFilter || hasIts || hasPhones)),
    staleTime: 30000,
  });
};

export const usePreviewEventRsvpRecipients = ({
  eventId,
  filterCriteria,
  limit = 10000,
  offset = 0,
  enabled = false,
}: {
  eventId: string | undefined;
  filterCriteria?: FilterGroupCriteriaShape | null;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) => {
  const dataProvider = useDataProvider();

  const hasFilter =
    filterCriteria &&
    ((filterCriteria.rules?.length ?? 0) > 0 || (filterCriteria.groups?.length ?? 0) > 0);

  const selectionKey = JSON.stringify({
    eventId,
    filterCriteria: hasFilter ? filterCriteria : null,
  });

  return useQuery({
    queryKey: ["whatsappBroadcasts", "previewEventRsvpRecipients", selectionKey, limit, offset],
    queryFn: async () => {
      if (!eventId?.trim()) {
        throw new Error("Select an event first");
      }
      return dataProvider.previewEventRsvpRecipients("whatsappBroadcasts", {
        eventId: eventId.trim(),
        filterCriteria: hasFilter ? filterCriteria : undefined,
        limit,
        offset,
      });
    },
    enabled: Boolean(enabled && eventId?.trim()),
    staleTime: 30000,
  });
};

export const usePreviewFmbOutstandingRecipients = ({
  hijriYearStart,
  minOutstandingPercent,
  thaliTypeIds,
  deliveryScheduleProfileIds,
  itsIds,
  notifyMode,
  limit = 10000,
  offset = 0,
  enabled = false,
}: {
  hijriYearStart?: number | null;
  minOutstandingPercent?: number | null;
  thaliTypeIds?: string[] | null;
  deliveryScheduleProfileIds?: string[] | null;
  itsIds?: string[] | null;
  notifyMode?: string | null;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) => {
  const dataProvider = useDataProvider();

  const selectionKey = JSON.stringify({
    hijriYearStart,
    minOutstandingPercent,
    thaliTypeIds,
    deliveryScheduleProfileIds,
    itsIds,
    notifyMode,
  });

  return useQuery({
    queryKey: [
      "whatsappBroadcasts",
      "previewFmbOutstandingRecipients",
      selectionKey,
      limit,
      offset,
    ],
    queryFn: async () =>
      dataProvider.previewFmbOutstandingRecipients("whatsappBroadcasts", {
        hijriYearStart: hijriYearStart ?? undefined,
        minOutstandingPercent: minOutstandingPercent ?? undefined,
        thaliTypeIds: thaliTypeIds ?? undefined,
        deliveryScheduleProfileIds: deliveryScheduleProfileIds ?? undefined,
        itsIds: itsIds ?? undefined,
        notifyMode: notifyMode ?? undefined,
        limit,
        offset,
      }),
    enabled: Boolean(enabled),
    staleTime: 30000,
  });
};

export const usePreviewSabilOutstandingRecipients = ({
  minOutstandingPercent,
  itsIds,
  excludeItsIds,
  notifyMode,
  sabilType,
  limit = OUTSTANDING_PREVIEW_FETCH_LIMIT,
  offset = 0,
  enabled = false,
}: {
  minOutstandingPercent?: number | null;
  itsIds?: string[] | null;
  excludeItsIds?: string[] | null;
  notifyMode?: string | null;
  sabilType?: string | null;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) => {
  const dataProvider = useDataProvider();

  const selectionKey = JSON.stringify({
    minOutstandingPercent,
    itsIds,
    excludeItsIds,
    notifyMode,
    sabilType,
  });

  return useQuery({
    queryKey: [
      "whatsappBroadcasts",
      "previewSabilOutstandingRecipients",
      selectionKey,
      limit,
      offset,
    ],
    queryFn: async () =>
      dataProvider.previewSabilOutstandingRecipients("whatsappBroadcasts", {
        minOutstandingPercent: minOutstandingPercent ?? undefined,
        itsIds: itsIds ?? undefined,
        excludeItsIds: excludeItsIds ?? undefined,
        notifyMode: notifyMode ?? undefined,
        sabilType: sabilType ?? undefined,
        limit,
        offset,
      }),
    enabled: Boolean(enabled && sabilType),
    staleTime: 30000,
  });
};

export const usePreviewMuttavatteenOutstandingRecipients = ({
  minOutstandingPercent,
  itsIds,
  excludeItsIds,
  notifyMode,
  sabilType,
  limit = OUTSTANDING_PREVIEW_FETCH_LIMIT,
  offset = 0,
  enabled = false,
}: {
  minOutstandingPercent?: number | null;
  itsIds?: string[] | null;
  excludeItsIds?: string[] | null;
  notifyMode?: string | null;
  sabilType?: string | null;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) => {
  const dataProvider = useDataProvider();

  const selectionKey = JSON.stringify({
    minOutstandingPercent,
    itsIds,
    excludeItsIds,
    notifyMode,
    sabilType,
  });

  return useQuery({
    queryKey: [
      "whatsappBroadcasts",
      "previewMuttavatteenOutstandingRecipients",
      selectionKey,
      limit,
      offset,
    ],
    queryFn: async () =>
      dataProvider.previewMuttavatteenOutstandingRecipients("whatsappBroadcasts", {
        minOutstandingPercent: minOutstandingPercent ?? undefined,
        itsIds: itsIds ?? undefined,
        excludeItsIds: excludeItsIds ?? undefined,
        notifyMode: notifyMode ?? undefined,
        sabilType: sabilType ?? undefined,
        limit,
        offset,
      }),
    enabled: Boolean(enabled && sabilType),
    staleTime: 30000,
  });
};

export type TemplateBodyRecord = RaRecord & { bodyText?: string };

export const useTemplateValidation = (
  templateName: string | undefined,
  template: TemplateBodyRecord | undefined,
  isLoadingTemplate: boolean,
  parameters: Record<string, BroadcastParameter> | undefined,
  csvColumnHeaders: string[] = [],
  options?: { disabled?: boolean }
) => {
  const expectedVariables = useMemo(() => {
    if (!template?.bodyText) return [];
    return extractTemplateVariables(template.bodyText);
  }, [template?.bodyText]);

  const validationResult = useMemo(() => {
    if (options?.disabled) {
      return {
        isValid: true,
        errorMessage: null as string | null,
        expectedVariables: [] as number[],
        validationErrors: {} as Record<string, string>,
      };
    }
    if (!templateName) {
      return {
        isValid: false,
        errorMessage: "Please select a template before proceeding.",
        expectedVariables: [] as number[],
        validationErrors: {} as Record<string, string>,
      };
    }

    if (isLoadingTemplate) {
      return {
        isValid: false,
        errorMessage: "Loading template...",
        expectedVariables: [] as number[],
        validationErrors: {} as Record<string, string>,
      };
    }

    const result = validateTemplateParameters(
      template?.bodyText || "",
      parameters,
      csvColumnHeaders || []
    );

    return {
      isValid: result.isValid,
      errorMessage: result.isValid ? null : getMissingParametersMessage(result.missingParams),
      expectedVariables,
      validationErrors: result.errors,
    };
  }, [
    options?.disabled,
    templateName,
    isLoadingTemplate,
    template?.bodyText,
    parameters,
    csvColumnHeaders,
    expectedVariables,
  ]);

  return validationResult;
};

export type LookupItsResult = {
  matched: RaRecord[];
  unmatched: string[];
  count: number;
};

export const useLookupIts = () => {
  const dataProvider = useDataProvider();
  const [data, setData] = useState<LookupItsResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const lookup = useCallback(
    async (itsIds: string[]) => {
      if (!itsIds || itsIds.length === 0) {
        setData({ matched: [], unmatched: [], count: 0 });
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const result = (await dataProvider.lookupIts("whatsappBroadcasts", {
          itsIds,
        })) as LookupItsResult;
        setData({
          matched: (result.matched as RaRecord[]) ?? [],
          unmatched: (result.unmatched as string[]) ?? [],
          count: result.count ?? 0,
        });
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    },
    [dataProvider]
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  return { data, isLoading, error, lookup, reset };
};
